import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { authApi } from '../../api/auth';
import { useAuthStore } from '../../stores/auth';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../i18n/errors';

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const setSession = useAuthStore((state) => state.setSession);
  const [resent, setResent] = useState(false);

  const token = params.get('token');

  const verify = useMutation({
    mutationFn: (value: string) => authApi.verifyEmail(value),
    onSuccess: (session) => {
      setSession(session.user);
      navigate('/', { replace: true });
    },
  });

  useEffect(() => {
    if (token) verify.mutate(token);
    // Один прогон на приход токена: verify.mutate стабильна между рендерами.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const resend = useMutation({
    mutationFn: () => authApi.resendVerification(user?.email ?? ''),
    onSuccess: () => setResent(true),
  });

  if (token) {
    return (
      <div className="flex flex-col gap-5">
        <h1 className="text-section-title font-semibold">{t('auth.verify.title')}</h1>
        {verify.isPending ? <p className="text-body text-content-secondary">{t('auth.verify.checking')}</p> : null}
        {verify.isError ? (
          <>
            <Alert tone="error">{errorText(verify.error)}</Alert>
            <Button variant="primary" onClick={() => navigate('/verify-email', { replace: true })}>{t('auth.verify.requestNew')}</Button>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <h1 className="text-section-title font-semibold">{t('auth.verify.prompt')}</h1>
      <p className="text-body text-content-secondary">
        {user?.email
          ? t('auth.verify.instructionTo', { email: user.email })
          : t('auth.verify.instruction')}
      </p>

      {resent ? <Alert tone="success">{t('auth.verify.resent')}</Alert> : null}
      {resend.isError ? (
        <Alert tone="error">{errorText(resend.error)}</Alert>
      ) : null}

      <div className="flex gap-3">
        <Button onClick={() => resend.mutate()} disabled={resend.isPending || !user?.email}>
          {resend.isPending ? t('auth.sending') : t('auth.verify.resend')}
        </Button>
        <Button variant="tertiary" onClick={() => navigate('/')}>{t('common.continue')}</Button>
      </div>
    </div>
  );
}
